import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import "./HomePage.css"; // reuse the animated text style

export default function ContentPolicyPage() {
  return (
    <Box marginTop={4}>
      <Stack
        justifyContent="center"
        alignItems="center"
        gap={{ xs: 3, md: 5 }}
        paddingX={0}
      >
        {/* Animated main heading */}
        <Typography
          className="animate-character"
          variant={"h3"}
          fontWeight="bold"
          align="center"
          sx={{
            "@media (max-width: 600px)": {
              fontSize: "2.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 601px) and (max-width: 900px)": {
              fontSize: "3.2rem",
              letterSpacing: 0.5,
            },
            "@media (min-width: 901px)": {
              fontSize: "4rem",
              letterSpacing: 0.5,
            },
          }}
        >
          Content Policy
        </Typography>

        {/* Decorative dividers */}
        <Box sx={{ width: "100%" }}>
          <Divider />
          <Divider />
        </Box>

        {/* Content container */}
        <Box
          sx={{
            width: { xs: "95%", md: "80%" },
            maxWidth: "900px",
            textAlign: "left",
          }}
        >
          {/* Introduction */}
          <Divider sx={{ width: "100%", mb: 2, mt: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Our Commitment to Respectful Content
            </Typography>
          </Divider>
          <Typography paragraph>
            SainiAudiobooks is dedicated to providing high‑quality Punjabi
            audiobooks and literary works. All content available on our
            platform is either owned by us, licensed from rights holders, or
            used with explicit permission. We respect intellectual property
            rights and expect our users to do the same.
          </Typography>

          {/* Restrictions */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              User Restrictions
            </Typography>
          </Divider>
          <Typography paragraph>
            By using SainiAudiobooks, you agree not to:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>
              Copy, reproduce, modify, or create derivative works from any
              content on the platform without express written permission.
            </li>
            <li>
              Distribute, publicly perform, or display any content except as
              permitted by your subscription.
            </li>
            <li>
              Record, capture, or share any part of the audiobooks or other
              materials through any means (including screen recording, audio
              capture, or third‑party applications).
            </li>
            <li>
              Use the content for commercial purposes, such as reselling,
              broadcasting, or including it in any other service.
            </li>
            <li>
              Attempt to circumvent any technical measures that protect the
              content (e.g., DRM, access controls).
            </li>
          </Typography>
          <Typography paragraph>
            Any violation of these restrictions may result in immediate
            termination of your account and legal action.
          </Typography>

          {/* User-Generated Content */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              User‑Generated Content
            </Typography>
          </Divider>
          <Typography paragraph>
            In certain areas (e.g., reviews, comments), you may submit content.
            By submitting, you grant SainiAudiobooks a worldwide, royalty‑free,
            non‑exclusive license to use, display, and distribute that content
            in connection with the Service. You represent that you own or have
            the necessary rights to any content you submit, and that it does not
            infringe on any third‑party rights.
          </Typography>
          <Typography paragraph>
            We reserve the right to remove any user‑generated content that is
            unlawful, abusive, defamatory, or otherwise violates this policy.
          </Typography>

          {/* Copyright Complaints */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Copyright Complaints
            </Typography>
          </Divider>
          <Typography paragraph>
            If you believe that any content on SainiAudiobooks infringes your
            copyright or other intellectual property rights, please notify us
            promptly. Your written notice should include:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>A description of the copyrighted work you claim has been infringed.</li>
            <li>
              Identification of the material you believe is infringing,
              including the URL or other specific location on the platform.
            </li>
            <li>Your contact information (name, address, email, phone).</li>
            <li>
              A statement that you have a good faith belief that the use is not
              authorised by the copyright owner, its agent, or the law.
            </li>
            <li>
              A statement, under penalty of perjury, that the information in
              your notice is accurate and that you are the copyright owner or
              authorised to act on the owner’s behalf.
            </li>
            <li>Your physical or electronic signature.</li>
          </Typography>
          <Typography paragraph>
            Send all notices to:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              my: 2,
              p: 2,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: 2,
              fontFamily: "monospace",
            }}
          >
            <strong>info@sainiaudiobooks.com</strong>
          </Typography>
          <Typography paragraph>
            We will respond to valid complaints within a reasonable time and
            will take appropriate action, which may include removing the
            disputed content and terminating accounts of repeat infringers.
          </Typography>

          {/* Repeat Infringers */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Repeat Infringers
            </Typography>
          </Divider>
          <Typography paragraph>
            It is our policy to terminate, in appropriate circumstances, the
            accounts of users who are repeat infringers of intellectual property
            rights.
          </Typography>

          {/* Contact for General Questions */}
          <Divider sx={{ width: "100%", mb: 2, mt: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              Questions
            </Typography>
          </Divider>
          <Typography paragraph>
            If you have any questions about this Content Policy, please contact
            us at:
            <br />
            <strong>Email:</strong> info@sainiaudiobooks.com
          </Typography>

          {/* Last updated */}
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}
          >
            Last Updated: 28 March 2026
          </Typography>
        </Box>

        {/* Back to Home button */}
        <Button
          size="large"
          variant="contained"
          color="primary"
          sx={{
            px: 4,
            py: 1.75,
            fontSize: "1.15rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            borderRadius: 9999,
            background: "linear-gradient(90deg, #0072ff 0%, #00c6ff 100%)",
            boxShadow:
              "0 6px 18px rgba(0,114,255,0.3), 0 2px 6px rgba(0,0,0,0.1)",
            textTransform: "none",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 10px 24px rgba(0,114,255,0.4), 0 4px 10px rgba(0,0,0,0.12)",
              background:
                "linear-gradient(90deg, #0062e6 0%, #00b6ff 100%) !important",
            },
            "&:focus-visible": {
              outline: "3px solid #111",
              outlineOffset: "3px",
            },
          }}
          component={Link}
          to="/"
          aria-label="Return to homepage"
        >
          ← Return to Home
        </Button>

        {/* Bottom decorative dividers */}
        <Box sx={{ width: "100%", mt: 4 }}>
          <Divider />
          <Divider />
        </Box>
      </Stack>
    </Box>
  );
}